import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import type { ComponentType } from 'react';
import {
  Calendar,
  ClipboardList,
  FileStack,
  LogOut,
  NotebookText,
  Stethoscope,
  User,
  FileDown,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePatientContextStore } from '@/stores/patient-context-store';

const navItems = [
  { to: '/pep-vida/agenda', label: 'Minha agenda', icon: Calendar },
] as const;

export function Sidebar() {
  const { user, logout } = useAuth();
  const { location } = useRouterState();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const { patient } = usePatientContextStore();

  return (
    <aside className="hidden md:flex md:w-[230px] md:flex-col md:border-r md:bg-white">
      <div className="flex h-16 items-center gap-2 border-b px-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Stethoscope className="h-4 w-4" />
        </div>
        <div>
          <div className="text-sm font-semibold">PEP-VIDA</div>
          <div className="text-xs text-muted-foreground">Grupo Vida</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-auto p-4">
        {navItems.map((item) => {
          const active = currentPath.startsWith(item.to);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
        {user?.role === 'admin' && (
          <>
            <Link to="/pep-vida/modelos-documentos" className={cn('flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors', currentPath.startsWith('/pep-vida/modelos-documentos') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')}>
              <SlidersHorizontal className="h-4 w-4" />
              Parametrização
            </Link>
            <Link to="/pep-vida/prontuarios-exportacao" className={cn('flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors', currentPath.startsWith('/pep-vida/prontuarios-exportacao') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground')}>
              <FileDown className="h-4 w-4" />
              Exportar prontuário
            </Link>
          </>
        )}

        {patient && shouldShowPatientCard(currentPath, patient.cdPaciente) && (
          <div className="mt-6 rounded-lg border bg-background p-3 shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Paciente em atendimento
            </div>
            <div className="mt-2 truncate text-sm font-semibold">{patient.nome}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              {patient.meta ?? `Paciente ${patient.cdPaciente}`}
            </div>
            {patient.status && (
              <Badge variant="consulta" className="mt-2">
                {patient.status}
              </Badge>
            )}
            <div className="mt-3 space-y-1">
              <ContextLink
                icon={NotebookText}
                label="Atendimento"
                onClick={() => {
                  const activeEncounterId =
                    patient.clinicalEncounterId ||
                    sessionStorage.getItem(`pep-vida:consulta-ativa:${patient.cdPaciente}`);
                  if (activeEncounterId) {
                    navigate({
                      to: '/pep-vida/consultas/$id',
                      params: { id: activeEncounterId },
                    });
                    return;
                  }
                  navigate({
                    to: '/pep-vida/pacientes/$cdPaciente',
                    params: { cdPaciente: String(patient.cdPaciente) },
                  });
                }}
                active={
                  currentPath.includes('/consultas/') ||
                  currentPath === `/pep-vida/pacientes/${patient.cdPaciente}`
                }
              />
              <ContextLink
                icon={ClipboardList}
                label="Histórico"
                onClick={() =>
                  navigate({
                    to: '/pep-vida/pacientes/$cdPaciente/historico',
                    params: { cdPaciente: String(patient.cdPaciente) },
                  })
                }
                active={currentPath.includes('/historico')}
              />
              <ContextLink
                icon={Calendar}
                label="Exames e laudos"
                onClick={() =>
                  navigate({
                    to: '/pep-vida/pacientes/$cdPaciente/exames',
                    params: { cdPaciente: String(patient.cdPaciente) },
                  })
                }
                active={currentPath.includes('/exames')}
              />
              <ContextLink
                icon={FileStack}
                label="Documentos"
                onClick={() =>
                  navigate({
                    to: '/pep-vida/pacientes/$cdPaciente/documentos',
                    params: { cdPaciente: String(patient.cdPaciente) },
                  })
                }
                active={currentPath.includes('/documentos')}
              />
            </div>
          </div>
        )}
      </nav>

      {user && (
        <div className="border-t p-4">
          <div className="flex items-center gap-3 rounded-md p-2 text-sm">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              <User className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium">{user.login}</div>
              <Badge variant="outline" className="text-xs capitalize">
                {user.role}
              </Badge>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2 w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
            onClick={() => logout()}
          >
            <LogOut className="h-4 w-4" />
            Sair
          </Button>
        </div>
      )}
    </aside>
  );
}

/**
 * Define se o cartao do paciente deve aparecer na Sidebar.
 *
 * Regra do projeto: o cartao so aparece quando o usuario esta em uma
 * rota CLINICA do paciente (detalhe, consulta, historico, exames ou
 * documentos). NAO aparece em Dashboard, Agenda, lista de pacientes
 * ou outras rotas administrativas.
 */
function shouldShowPatientCard(currentPath: string, cdPaciente: number): boolean {
  // Paciente detalhe (rota exata ou com sufixo /historico, /exames, /documentos)
  if (currentPath === `/pep-vida/pacientes/${cdPaciente}`) return true;
  if (currentPath.startsWith(`/pep-vida/pacientes/${cdPaciente}/`)) return true;
  // Consulta (qualquer encounter ativo)
  if (currentPath.startsWith('/pep-vida/consultas/')) return true;
  return false;
}

function ContextLink({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  active: boolean;
  onClick?: () => void;
}) {
  const className = cn(
    'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
    active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
  );
  const content = (
    <>
      <Icon className="h-3.5 w-3.5" />
      {label}
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(className, 'w-full text-left')}>
        {content}
      </button>
    );
  }

  return (
    <div className={className}>
      {content}
    </div>
  );
}
